'use client';

import { useCallback, useState } from 'react';

import { useSyncedCountdown } from '../../../utils/useSyncedCountdown';
import { Button } from '../../button';
import { Resource, ResourceBar, ResourceBody } from '../../resource';
import { Question } from '../layout/Question';
import { QuestionToolbar } from '../layout/QuestionToolbar';
import { useOptimisticBookmark } from '../useOptimisticBookmark';
import type { MultipleChoiceQuestionProps } from './MultipleChoiceQuestion.types';

import styles from './MultipleChoiceQuestion.module.scss';

function MultipleChoiceQuestion({
  style,
  className,
  timeInSeconds = 0,
  question,
  isSubmitting = false,
  onBookmark,
  onUnbookmark,
  onSubmit,
  onClose,
  testId = 'ui-multiple-choice-question',
}: MultipleChoiceQuestionProps) {
  const [selectedOptionIndex, setSelectedOptionIndex] = useState(
    question.selectedOptionIndex,
  );

  // A new question is answered from scratch. Adjusted while rendering rather than in an
  // effect, so it is never painted carrying the last question's answer.
  const [askedQuestionId, setAskedQuestionId] = useState(question.id);

  if (askedQuestionId !== question.id) {
    setAskedQuestionId(question.id);
    setSelectedOptionIndex(question.selectedOptionIndex);
  }

  const { bookmarked, toggle: toggleBookmark } = useOptimisticBookmark({
    id: question.id,
    bookmarked: question.bookmarked,
    onBookmark,
    onUnbookmark,
  });

  const submitWith = useCallback(
    (remainingInSeconds: number) => {
      onSubmit({
        id: question.id,
        answerIndex: selectedOptionIndex,
        timeInSeconds: timeInSeconds - remainingInSeconds,
      });
    },
    [onSubmit, question.id, selectedOptionIndex, timeInSeconds],
  );

  const countdown = useSyncedCountdown({
    timeInSeconds,
    onExpire: () => submitWith(0),
  });

  const { remaining, stop, secondsLeft } = countdown;

  const handleSubmit = useCallback(() => {
    const left = remaining();

    stop();
    submitWith(left);
  }, [remaining, stop, submitWith]);

  const handleResponse = useCallback((index: number) => {
    // Toggle off if clicking the same option, otherwise select the new option
    setSelectedOptionIndex((current) => (current === index ? 0 : index));
  }, []);

  return (
    <Resource style={style} className={className} testId={testId}>
      <QuestionToolbar
        secondsLeft={secondsLeft}
        bookmarked={bookmarked}
        onBookmark={toggleBookmark}
        close={onClose && { closeConfirmation: true, onClose }}
      />
      <ResourceBody key={question.id}>
        <Question
          question={{ ...question, selectedOptionIndex }}
          onResponse={handleResponse}
        />
      </ResourceBody>
      <ResourceBar
        edge="bottom"
        layout="split"
        end={
          <Button
            className={styles['button']}
            variant="outline-primary"
            onClick={handleSubmit}
            disabled={isSubmitting}
            loading={isSubmitting}
          >
            Corregir
          </Button>
        }
      />
    </Resource>
  );
}

export { MultipleChoiceQuestion };
